import { FC, useEffect } from 'react'
import useApolloClient from 'src/api/apollo-client/apollo-client.hook'
import { ApolloProvider } from '@apollo/client'
import useCurrentLocale from './hooks/current-locale/use-current-locale'
import { AuthProvider } from './hooks/auth/auth.context'
import { BrowserRouter, useSearchParams } from 'react-router-dom'
import { RouterConfig } from './navigation/router-config'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { LicenseInfo, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { useAuth } from './hooks/auth/auth.hook'
import { SnackbarProvider } from 'notistack'
import { CartProvider } from './hooks/context/cart/cart.context'
import { TenantIdAndPropertyIdProvider } from './hooks/context/tenantIdAndPropertyId/tenantIdAndPropertyId.context'
import i18n from 'src/locales/i18n'

document.documentElement.lang = i18n.language

function App() {
  LicenseInfo.setLicenseKey('cb3e4f7fda3ad7c6f6e1acb600b9dd2aTz04NDE2NyxFPTE3Mzk0NDc5MjAwMDAsUz1wcmVtaXVtLExNPXN1YnNjcmlwdGlvbixLVj0y')

  return (
    <AuthProvider>
      <AppProviders>
        <BrowserRouter>
          <AuthLoader>
            <CartProvider>
              <TenantIdAndPropertyIdProvider>
                <RouterConfig />
              </TenantIdAndPropertyIdProvider>
            </CartProvider>
          </AuthLoader>
        </BrowserRouter>
      </AppProviders>
    </AuthProvider>
  )
}

export default App

const AppProviders: FC = ({ children }) => {
  const apolloClient = useApolloClient()
  const { dateFnsLocale } = useCurrentLocale()

  return (
    <ApolloProvider client={apolloClient}>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
        <SnackbarProvider maxSnack={3}>{children}</SnackbarProvider>
      </LocalizationProvider>
    </ApolloProvider>
  )
}

const AuthLoader: FC = ({ children }) => {
  const [searchParams] = useSearchParams()
  const { setAuth } = useAuth()

  useEffect(() => {
    if (!process.env.REACT_APP_TOKEN_PARAM_NAME) {
      throw new Error('Missing env variable REACT_APP_TOKEN_PARAM_NAME.')
    }
    const token = searchParams.get(process.env.REACT_APP_TOKEN_PARAM_NAME)
    if (token) {
      try {
        const decodedToken = JSON.parse(window.atob(token.split('.')[1]))
        setAuth({ loading: false, isAuthenticated: true, token, reservationId: decodedToken.reservationId, quotationId: decodedToken.reservationId })
      } catch (e) {
        setAuth({ loading: false, isAuthenticated: false })
      }
    } else {
      setAuth({ loading: false, isAuthenticated: false })
    }
  }, [searchParams, setAuth])

  return <>{children}</>
}
